/**
 * Authenticated application shell: sidebar navigation, top bar with global search,
 * notifications, team switcher and account menu. The sidebar becomes an offcanvas
 * menu on small screens.
 */
import type { Permission } from "@shared/permissions.ts";
import { useEffect, useRef, useState } from "react";
import { Alert, Badge, Button, Dropdown, Form, ListGroup, Offcanvas } from "react-bootstrap";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { api } from "../../lib/api.ts";
import { useAuth } from "../../lib/auth.tsx";
import { useFormat } from "../../lib/format.ts";
import { useApi, useInvalidateAll } from "../../lib/query.ts";
import { BillingBanner } from "../../pages/settings/BillingPage.tsx";
import { StepUpProvider } from "../auth/SecondFactor.tsx";
import { Icon } from "../ui/Icon.tsx";

interface NavItem {
  to: string;
  label: string;
  icon: string;
  permission?: Permission;
}

const NAV: Array<{ heading?: string; items: NavItem[] }> = [
  { items: [{ to: "/", label: "Dashboard", icon: "dashboard" }] },
  {
    heading: "Portfolio",
    items: [
      { to: "/properties", label: "Properties", icon: "home", permission: "properties:read" },
      { to: "/tenancies", label: "Tenancies", icon: "key", permission: "tenancies:read" },
      { to: "/tenants", label: "Tenants", icon: "users", permission: "tenancies:read" },
      { to: "/owners", label: "Owners", icon: "owner", permission: "owners:read" },
    ],
  },
  {
    heading: "Money",
    items: [
      { to: "/income", label: "Income", icon: "income", permission: "income:read" },
      { to: "/expenses", label: "Expenses", icon: "expense", permission: "expenses:read" },
      { to: "/finance", label: "Mortgages & finance", icon: "finance", permission: "finance:read" },
    ],
  },
  {
    heading: "Operations",
    items: [
      { to: "/tasks", label: "Tasks", icon: "tasks", permission: "tasks:read" },
      { to: "/maintenance", label: "Maintenance", icon: "tool", permission: "maintenance:read" },
      { to: "/communications", label: "Communications", icon: "message", permission: "communications:read" },
      { to: "/compliance", label: "Compliance", icon: "shield", permission: "compliance:read" },
      { to: "/documents", label: "Documents", icon: "file", permission: "documents:read" },
      { to: "/contractors", label: "Contractors", icon: "briefcase", permission: "contractors:read" },
      { to: "/timeline", label: "Activity", icon: "activity" },
    ],
  },
  {
    heading: "Insight",
    items: [
      { to: "/reports", label: "Reports", icon: "chart", permission: "reports:read" },
      { to: "/tax", label: "Tax", icon: "percent", permission: "tax:read" },
    ],
  },
  { items: [{ to: "/settings", label: "Settings", icon: "settings" }] },
];

function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  const { can } = useAuth();
  return (
    <nav aria-label="Main navigation" className="sidebar-nav">
      {NAV.map((group, gi) => {
        const items = group.items.filter((i) => !i.permission || can(i.permission));
        if (items.length === 0) return null;
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: static navigation
          <div key={gi} className="mb-2">
            {group.heading && <div className="sidebar-heading">{group.heading}</div>}
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className="sidebar-link"
                onClick={onNavigate}
              >
                <Icon name={item.icon} size={17} />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </div>
        );
      })}
    </nav>
  );
}

interface SearchResult {
  type: string;
  id: string;
  title: string;
  subtitle: string;
  link: string;
}

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (e.key === "/" && !["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) {
        e.preventDefault();
        input.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const results = useApi<SearchResult[]>(debounced.length >= 2 ? "/api/search" : null, { q: debounced });
  const go = (link: string) => {
    setOpen(false);
    setQ("");
    navigate(link);
  };
  return (
    <div className="global-search position-relative flex-grow-1" style={{ maxWidth: 480 }}>
      <Form
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          if (q.trim().length >= 2) go(`/search?q=${encodeURIComponent(q.trim())}`);
        }}
      >
        <div className="position-relative">
          <span className="search-icon">
            <Icon name="search" size={16} />
          </span>
          <Form.Control
            ref={input}
            type="search"
            placeholder="Search properties, tenants, documents…  ( / )"
            aria-label="Search"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            className="ps-5"
          />
        </div>
      </Form>
      {open && debounced.length >= 2 && (
        <div className="search-results shadow">
          {results.isLoading && <div className="p-3 small text-muted">Searching…</div>}
          {results.data && results.data.length === 0 && (
            <div className="p-3 small text-muted">No results for “{debounced}”.</div>
          )}
          <ListGroup variant="flush">
            {results.data?.map((r) => (
              <ListGroup.Item key={`${r.type}-${r.id}`} action onMouseDown={() => go(r.link)}>
                <div className="d-flex justify-content-between gap-2">
                  <span className="text-truncate">{r.title}</span>
                  <Badge bg="light" text="dark" className="text-capitalize">
                    {r.type}
                  </Badge>
                </div>
                {r.subtitle && <div className="small text-muted text-truncate">{r.subtitle}</div>}
              </ListGroup.Item>
            ))}
          </ListGroup>
        </div>
      )}
    </div>
  );
}

interface NotificationList {
  items: Array<{
    id: string;
    type: string;
    title: string;
    body: string;
    link: string;
    readAt: string | null;
    createdAt: string;
  }>;
  unread: number;
}

function NotificationBell() {
  const q = useApi<NotificationList>("/api/notifications", { limit: 10 });
  const invalidate = useInvalidateAll();
  const navigate = useNavigate();
  const f = useFormat();
  const unread = q.data?.unread ?? 0;
  return (
    <Dropdown align="end">
      <Dropdown.Toggle
        variant="light"
        className="position-relative no-caret"
        aria-label={`Notifications (${unread} unread)`}
      >
        <Icon name="bell" />
        {unread > 0 && (
          <Badge bg="danger" pill className="notification-count">
            {unread > 99 ? "99+" : unread}
          </Badge>
        )}
      </Dropdown.Toggle>
      <Dropdown.Menu className="notification-menu shadow">
        <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
          <strong>Notifications</strong>
          {unread > 0 && (
            <Button
              size="sm"
              variant="link"
              className="p-0"
              onClick={async () => {
                await api.post("/api/notifications/read-all");
                await invalidate();
              }}
            >
              Mark all read
            </Button>
          )}
        </div>
        {(q.data?.items ?? []).length === 0 && (
          <div className="p-3 small text-muted">You're all caught up.</div>
        )}
        {q.data?.items.map((n) => (
          <Dropdown.Item
            key={n.id}
            className={`notification-item ${n.readAt ? "" : "unread"}`}
            onClick={async () => {
              await api.post(`/api/notifications/${n.id}/read`);
              await invalidate();
              if (n.link) navigate(n.link);
            }}
          >
            <div className="fw-semibold text-wrap">{n.title}</div>
            {n.body && <div className="small text-muted text-wrap">{n.body}</div>}
            <div className="small text-muted">{f.date(n.createdAt)}</div>
          </Dropdown.Item>
        ))}
        <Dropdown.Divider />
        <Dropdown.Item as={Link} to="/notifications" className="text-center small">
          View all notifications
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
}

function AccountMenu() {
  const { me, logout, switchTeam } = useAuth();
  const navigate = useNavigate();
  if (!me) return null;
  const active = me.teams.find((t) => t.id === me.activeTeamId);
  return (
    <Dropdown align="end">
      <Dropdown.Toggle variant="light" className="d-flex align-items-center gap-2" aria-label="Account menu">
        <span className="avatar">{me.user.name.slice(0, 1).toUpperCase()}</span>
        <span className="d-none d-lg-inline text-start lh-sm">
          <span className="d-block small fw-semibold">{me.user.name}</span>
          <span className="d-block small text-muted">{active?.name}</span>
        </span>
      </Dropdown.Toggle>
      <Dropdown.Menu className="shadow">
        <Dropdown.Header>{me.user.email}</Dropdown.Header>
        {me.teams.length > 1 && (
          <>
            <Dropdown.Header>Switch team</Dropdown.Header>
            {me.teams.map((t) => (
              <Dropdown.Item
                key={t.id}
                active={t.id === me.activeTeamId}
                onClick={async () => {
                  await switchTeam(t.id);
                  navigate("/");
                }}
              >
                {t.name} <span className="small text-muted">· {t.roleName}</span>
              </Dropdown.Item>
            ))}
            <Dropdown.Divider />
          </>
        )}
        <Dropdown.Item as={Link} to="/settings/account">
          Account settings
        </Dropdown.Item>
        {me.isStaff && (
          <Dropdown.Item as={Link} to="/staff">
            Staff console
          </Dropdown.Item>
        )}
        <Dropdown.Item as={Link} to="/settings/notifications">
          Notification preferences
        </Dropdown.Item>
        <Dropdown.Divider />
        <Dropdown.Item
          onClick={async () => {
            await logout();
            navigate("/login", { replace: true });
          }}
        >
          <Icon name="logout" size={14} /> Sign out
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
}

/** The team requires two-step verification (#43) and this member hasn't set it up yet. */
function MfaRequiredNotice() {
  return (
    <div className="text-center py-5">
      <h1 className="h4">Set up two-step verification</h1>
      <p className="text-muted">
        This team requires every member to use two-step verification. It only takes a minute.
      </p>
      <Link className="btn btn-primary" to="/settings/account">
        Set it up
      </Link>
    </div>
  );
}

/** A suspended team (#44) can only reach billing and export. */
function SuspendedNotice() {
  return (
    <div className="text-center py-5">
      <h1 className="h4">This team is suspended</h1>
      <p className="text-muted">
        Payment is overdue, so the team's records are unavailable until it's settled. Nothing has been
        deleted.
      </p>
      <div className="d-flex justify-content-center gap-2">
        <Link className="btn btn-primary" to="/settings/billing">
          Plan &amp; billing
        </Link>
        <Link className="btn btn-outline-secondary" to="/settings/export">
          Export your data
        </Link>
      </div>
    </div>
  );
}

export function AppLayout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { me } = useAuth();
  const mfaNeeded =
    Boolean(me?.mfa.requiredByTeam && !me.mfa.enabled) && location.pathname !== "/settings/account";
  const suspended =
    me?.billing?.state === "suspended" &&
    !["/settings/billing", "/settings/export"].includes(location.pathname);
  // Close the mobile menu and focus the page on navigation.
  // biome-ignore lint/correctness/useExhaustiveDependencies: react to route changes only
  useEffect(() => {
    setMenuOpen(false);
    document.getElementById("main")?.focus({ preventScroll: true });
    window.scrollTo(0, 0);
  }, [location.pathname]);
  return (
    <div className="app-shell">
      <a href="#main" className="visually-hidden-focusable skip-link">
        Skip to content
      </a>
      <aside className="sidebar d-none d-lg-flex flex-column">
        <Link to="/" className="brand">
          <img src="/favicon.svg" alt="" width={28} height={28} /> Property Manager
        </Link>
        <SidebarNav />
      </aside>
      <Offcanvas show={menuOpen} onHide={() => setMenuOpen(false)} className="sidebar-offcanvas">
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Property Manager</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <SidebarNav onNavigate={() => setMenuOpen(false)} />
        </Offcanvas.Body>
      </Offcanvas>
      <div className="main-column">
        <header className="topbar d-flex align-items-center gap-2">
          <Button
            variant="light"
            className="d-lg-none"
            onClick={() => setMenuOpen(true)}
            aria-label="Open menu"
          >
            <Icon name="menu" />
          </Button>
          <GlobalSearch />
          <div className="ms-auto d-flex align-items-center gap-2">
            <NotificationBell />
            <AccountMenu />
          </div>
        </header>
        <StepUpProvider />
        <BillingBanner />
        {me?.emailSuppressed && (
          <Alert variant="warning" className="mb-0 rounded-0 border-0 border-bottom small py-2">
            {me.emailSuppressed === "complaint"
              ? "Emails to your address were reported as spam, so we've stopped sending them."
              : "Emails to your address are bouncing, so we've stopped sending them."}{" "}
            Change your address under <Link to="/settings/account">Settings → Account</Link>, or contact
            support once your mailbox is working.
          </Alert>
        )}
        <main id="main" tabIndex={-1} className="content">
          {suspended ? <SuspendedNotice /> : mfaNeeded ? <MfaRequiredNotice /> : <Outlet />}
        </main>
      </div>
    </div>
  );
}
